import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { CreateChancel } from 'components';
import { FindLoad, FindSuccessItem, formatAMPMNeW, useModal, useSecondaryModal, Images } from 'utils';
import { appointmentActions, httpRequestsOnSuccessActions } from 'store';
import { renderClientName } from '../../../../utils';
import { reminderTargetEnum, signatureTypeEnums } from './constants';

const REMIND_APPT_SIGNATURE = 'REMIND_APPT_SIGNATURE';

const REMIND_CHANNEL = {
    PUSH: 'PUSH',
    EMAIL: 'EMAIL',
};

const CheckMark = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 3L4.5 8.5L2 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Checkbox = ({ checked, onChange, label }) => (
    <button type="button" className="sig-remind-checkbox" onClick={onChange}>
        <span className={`sig-remind-checkbox-box ${checked ? 'checked' : ''}`}>{checked && <CheckMark />}</span>
        <span className="sig-remind-checkbox-label">{label}</span>
    </button>
);

/** Read-only representation of the reminder email, built from the selected
 *  appointments. The email actually delivered is composed by the backend —
 *  this preview reflects the same data the recipient will be reminded about. */
const EmailPreview = ({ group }) => (
    <div className="sig-remind-preview">
        <p className="sig-remind-preview-title">Signature Reminder</p>
        <p className="sig-remind-preview-line">
            Hello, this is a reminder that {group?.client?.fullName || 'your client'} has appointment
            {group?.appts?.length !== 1 ? 's' : ''} awaiting a signature:
        </p>
        <div className="sig-remind-preview-appts">
            {group?.appts?.map((appt) => (
                <div key={appt?.id} className="sig-remind-preview-appt">
                    <span className="sig-remind-preview-appt-id">{appt?.displayId}</span>
                    <span>
                        {moment.utc(appt?.startDate).format('M/D/YY')}, {formatAMPMNeW(appt?.startTime)}-{formatAMPMNeW(appt?.endTime)}
                    </span>
                </div>
            ))}
        </div>
        <p className="sig-remind-preview-line">Please review and sign at your earliest convenience.</p>
    </div>
);

export const SignatureReminder = ({
    selectedList = [],
    selectedFullList = [],
    afterSuccess,
    single,
    reminderType = reminderTargetEnum.CLIENT,
}) => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const { openSecondary } = useSecondaryModal();
    const [channels, setChannels] = useState({ [REMIND_CHANNEL.PUSH]: true, [REMIND_CHANNEL.EMAIL]: true });
    const loading = FindLoad(REMIND_APPT_SIGNATURE);
    const success = FindSuccessItem(REMIND_APPT_SIGNATURE);
    const isProvider = reminderType === reminderTargetEnum.PROVIDER;
    const recipientNoun = isProvider ? 'provider' : 'client';

    // Which signature the reminder targets: the provider's own signature for a
    // provider reminder, otherwise the client/guardian signature. The backend
    // `SignatureRemindDTO` keys on `signatureType` (SIGNATURE | PROVIDER_SIGNATURE).
    const signatureType = isProvider ? signatureTypeEnums.PROVIDER_SIGNATURE : signatureTypeEnums.SIGNATURE;

    // Group the selected appointments by whoever is being reminded — the
    // provider (staff) for a provider reminder, otherwise the client/guardian.
    const recipientGroups = useMemo(() => {
        const map = new Map();
        (selectedFullList || []).forEach((appt) => {
            const recipient = isProvider ? appt?.staff : appt?.client;
            if (!recipient?.id) return;
            if (!map.has(recipient.id)) map.set(recipient.id, { recipient, appts: [] });
            map.get(recipient.id).appts.push(appt);
        });
        return Array.from(map.values());
    }, [selectedFullList, isProvider]);

    useEffect(() => {
        if (success) {
            close();
            afterSuccess();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(REMIND_APPT_SIGNATURE));
        }
    }, [success]);

    const toggleChannel = (channel) => setChannels((prev) => ({ ...prev, [channel]: !prev[channel] }));

    const notifyBy = Object.keys(channels).filter((key) => channels[key]);
    const emailSelected = channels[REMIND_CHANNEL.EMAIL];
    const canSend = !!notifyBy.length && !!selectedList?.length && !loading?.length;

    const handleSend = () => {
        if (!canSend) return;
        dispatch(appointmentActions.remindApptSignature({ apptIds: selectedList, notifyBy, signatureType }, single));
    };

    return (
        <div className="sig-remind-modal">
            {/* Header */}
            <div className="sig-remind-header">
                <div className="sig-remind-header-icon">
                    <img src={Images.notification} alt="Notifications" />
                </div>
                <div>
                    <p className="sig-remind-title">{isProvider ? 'Send Provider Reminders' : 'Send Client Reminders'}</p>
                    <p className="sig-remind-subtitle">
                        {isProvider
                            ? 'Remind providers to sign the selected appointments'
                            : 'Remind clients & guardians to sign the selected appointments'}
                    </p>
                </div>
            </div>

            <div className="sig-remind-section">
                <p className="sig-remind-section-title">Delivery Method</p>
                <div className="sig-remind-methods">
                    <Checkbox
                        checked={channels[REMIND_CHANNEL.PUSH]}
                        onChange={() => toggleChannel(REMIND_CHANNEL.PUSH)}
                        label="In-App Notification"
                    />
                    <Checkbox checked={channels[REMIND_CHANNEL.EMAIL]} onChange={() => toggleChannel(REMIND_CHANNEL.EMAIL)} label="Email" />
                </div>
            </div>

            <div className="sig-remind-section">
                <div className="sig-remind-recipients-head">
                    <p className="sig-remind-section-title" style={{ margin: 0 }}>
                        Recipients
                    </p>
                    <span className="sig-remind-recipients-count">
                        {recipientGroups.length} {recipientNoun}
                        {recipientGroups.length !== 1 ? 's' : ''} will receive reminders
                    </span>
                </div>

                <div className="sig-remind-recipients-list">
                    {recipientGroups.map((group) => (
                        <div key={group?.recipient?.id} className="sig-remind-recipient-row">
                            <div className="sig-remind-recipient-info">
                                <p className="sig-remind-recipient-name">
                                    {group?.recipient?.fullName || renderClientName(group?.recipient) || '--'}
                                </p>
                            </div>
                            {!single && (
                                <div className="sig-remind-recipient-right">
                                    <span className="sig-remind-appt-count">
                                        {group?.appts?.length} appt{group?.appts?.length !== 1 ? 's' : ''}
                                    </span>
                                    {/*{emailSelected && (*/}
                                    {/*    <button*/}
                                    {/*        type="button"*/}
                                    {/*        className="sig-remind-preview-btn"*/}
                                    {/*        onClick={() => openSecondary(<EmailPreview group={group} />)}>*/}
                                    {/*        Preview email*/}
                                    {/*    </button>*/}
                                    {/*)}*/}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="sig-remind-footer">
                <CreateChancel
                    create={'Send Reminders'}
                    chancel={'Cancel'}
                    onCreate={handleSend}
                    onClose={close}
                    disabled={!canSend}
                    loader={loading?.length}
                    buttonWidth={'100%'}
                />
            </div>
        </div>
    );
};
